import { useEffect, useRef, useState } from "react";

import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { ProductNewModel } from "../../models/products/product-new-model";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import Helpers from "../../utils/helpers";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import ProductService, {
  getProductWisePosOpenOrderQty,
} from "../../services/products/products.service";

interface PosMergedDTO {
  posId: number;
  posAlias: string;
  quantity: number;
  assignedOpenOrderQty: number;
}

interface ProductCardItemsProps {
  productData: ProductNewModel;
  showProductQuickView: (p: ProductNewModel) => void;
  buyNowItemDialog: (p: ProductNewModel, qty: number) => void;
  isAdmin?: boolean;
  cartItems: ProductCheckoutModel[];
  isCompact?: boolean; // New prop for compact mode
}

const fallbackImage = process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png";

const ProductCardItems = ({
  productData,
  showProductQuickView,
  buyNowItemDialog,
  isAdmin = false,
  cartItems = [],
  isCompact = false, // Default to false (Home Page)
}: ProductCardItemsProps) => {
  const [productDialog, setProductDialog] = useState<ProductNewModel>(productData);
  const [isHovered, setIsHovered] = useState(false);
  const [posDetails, setPosDetails] = useState<PosMergedDTO[]>([]);
  const [posLoading, setPosLoading] = useState(false);
  const [posError, setPosError] = useState<string | null>(null);
  const [hasFetchedPos, setHasFetchedPos] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [imageSource, setImageSource] = useState(fallbackImage);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const toast = useRef<Toast | null>(null);

  useEffect(() => {
    setProductDialog(productData);
    const url = productData.productImageList?.mediaUrl || fallbackImage;
    if (url !== imageSource) {
      setImageSource(url);
      setIsImageLoading(true);
    } else {
      // If the URL hasn't changed (e.g., still using fallback), ensure loading is false
      setIsImageLoading(false);
    }
    setPosDetails([]);
    setPosError(null);
    setHasFetchedPos(false);
    setIsFlipped(false);
    setIsHovered(false);
  }, [productData, imageSource]);

  const showMessage = (
    summary: string,
    detail: string,
    severity: "info" | "success" | "warn" | "error" = "info"
  ) => {
    toast.current?.show({ severity, summary, detail, life: 1200 });
  };

  const handleAddToCart = () => {
    if (productData.inStock === 0 || productData.availabilityPending) return;
      const isSalesCart = isAdmin && isCompact;

    if (isSalesCart) {
        const alreadyInSalesCart = cartItems.some(
        (item: ProductCheckoutModel) => item.id === productDialog.id
      );

      if (alreadyInSalesCart) {
        showMessage("Already Added", "Item already in Sales cart", "info");
        return;
      }
    } else {
    const cartStorageItems = ProductStorageHelpers.GetProductStorage() || [];
    const alreadyInCart = cartStorageItems.some(
      (item: ProductCheckoutModel) => item.id === productDialog.id
    );

    if (alreadyInCart) {
      showMessage("Already Added", "Item already in your cart", "info");
      return;
    }
  }

    showMessage("Added", "Item added successfully!", "success");

    buyNowItemDialog(productDialog, productDialog.minOrderQty || 1);
  };

  const loadPosIfNeeded = async () => {
    if (!isAdmin || !productDialog?.id || hasFetchedPos) return;

    setPosLoading(true);
    setPosError(null);

    try {
      const posQtyData = (await ProductService.getProductPOSQuantity(productDialog.id)) ?? [];
      const openOrderData: any[] = await getProductWisePosOpenOrderQty(productDialog.id);

      const merged = posQtyData.map((pos: any) => {
        const matchingOpenOrder = openOrderData.find(
          (openOrder) =>
            openOrder.posAlias?.toUpperCase() === pos.posAlias?.toUpperCase()
        );

        return {
          posId: pos.posId,
          posAlias: pos.posAlias,
          quantity: pos.quantity,
          assignedOpenOrderQty: matchingOpenOrder
            ? matchingOpenOrder.assignedOpenOrderQty
            : 0,
        };
      });

      const otherOrders = openOrderData.find(
        (openOrder) => openOrder.posAlias?.toUpperCase() === "OTHERS"
      );

      if (otherOrders) {
        merged.push({
          posId: -1,
          posAlias: "OTHERS",
          quantity: 0,
          assignedOpenOrderQty: otherOrders.assignedOpenOrderQty,
        });
      }

      setPosDetails(merged);
      setHasFetchedPos(true);
    } catch (error) {
      console.error(error);
      setPosError("Unable to load POS details");
    } finally {
      setPosLoading(false);
    }
  };

  const handleFlip = () => {
    // Non-admin users (including compact/sales-ordering mode) always open quick view
    if (!isAdmin) {
      showProductQuickView(productDialog);
      return;
    }

    // Admin users: flip card to show POS stock details
    loadPosIfNeeded();
    setIsFlipped((prev) => !prev);
  };

  // Style constants based on isCompact
  const imageHeight = isCompact ? "120px" : "240px";
  const titleSize = isCompact ? "13px" : "15px";
  const priceSize = isCompact ? "14px" : "18px";
  const buttonSize = isCompact ? "11px" : "13px";
  const buttonPadding = isCompact ? "4px 12px" : "8px 16px";
  const contentPadding = isCompact ? "6px 10px" : "16px";
  const showHoverIcons = !isCompact;
  const showDescription = !isCompact;

  return (
    <>
      <Toast ref={toast} />

      <div
        className="surface-card"
        style={{
          borderRadius: "16px",
          border: "1px solid #e5ebe5",
          boxShadow: isHovered
            ? "0 12px 24px rgba(15, 23, 42, 0.10)"
            : "0 4px 12px rgba(15, 23, 42, 0.05)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          background: "#ffffff",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderBottom: "1px solid #eef2ee",
          }}
        >
          {/* Top Left: Home Delivery Only */}
          {!isFlipped && !productDialog.courierAvailable && (
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: "12px",
                height: "32px",
                background: "#ffffff",
                color: "#52a83c",
                borderRadius: "32px",
                display: "flex",
                alignItems: "center",
                padding: "0 12px 0 0",
                justifyContent: "flex-start",
                fontSize: "11px",
                fontWeight: "600",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e8f5e9",
                zIndex: 3,
                cursor: "default",
                whiteSpace: "nowrap",
              }}
            >
              <i className="pi pi-home" style={{ fontSize: "14px", minWidth: "32px", textAlign: "center" }}></i>
              <span>Home delivery only</span>
            </div>
          )}

          {/* Top Right: Price excluding vessel (moved below description) */}

          <div
            className="cursor-pointer"
            onClick={handleFlip}
            style={{
              width: "100%",
              height: imageHeight,
              perspective: "1000px",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                transformStyle: "preserve-3d",
                transition: "transform 0.45s",
                transform: isAdmin && isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backfaceVisibility: "hidden",
                  background:
                    productDialog.inStock === 0
                      ? "linear-gradient(180deg, #e5e7eb 0%, #f3f4f6 100%)"
                      : "linear-gradient(180deg, #f8faf8 0%, #f2f6f3 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isImageLoading && (
                  <Skeleton
                    width="calc(100% - 32px)"
                    height="calc(100% - 32px)"
                    borderRadius="14px"
                  />
                )}

                <img
                  src={imageSource}
                  alt={productDialog.name}
                  onLoad={() => setIsImageLoading(false)}
                  onError={(event) => {
                    if (event.currentTarget.dataset.fallbackApplied === "true") {
                      setIsImageLoading(false);
                      return;
                    }
                    event.currentTarget.dataset.fallbackApplied = "true";
                    setImageSource(fallbackImage);
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    padding: "8px",
                    opacity: productDialog.inStock === 0 ? 0.55 : isImageLoading ? 0 : 1,
                    transition: "opacity 0.2s ease",
                    position: isImageLoading ? "absolute" : "relative",
                    inset: 0,
                  }}
                />

                {productDialog.inStock === 0 && !isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(185, 28, 28, 0.92)",
                        color: "#ffffff",
                        padding: "8px 18px",
                        borderRadius: "999px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      Out of Stock
                    </div>
                  </div>
                )}

                {isHovered && !isFlipped && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        showProductQuickView(productDialog);
                      }}
                      className="p-2 border-none cursor-pointer flex align-items-center justify-content-center"
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "999px",
                        background: "rgba(255,255,255,0.96)",
                        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                      }}
                      title="Quick View"
                    >
                      <i className="pi pi-eye text-600 font-bold" style={{ fontSize: "18px" }} />
                    </button>

                    {productDialog.inStock > 0 && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToCart();
                        }}
                        className="p-2 border-none cursor-pointer flex align-items-center justify-content-center"
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "999px",
                          background: "rgba(255,255,255,0.96)",
                          boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                        }}
                        title="Add to Cart"
                      >
                        <i
                          className="pi pi-shopping-cart text-600 font-bold"
                          style={{ fontSize: "18px" }}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  transform: "rotateY(180deg)",
                  backfaceVisibility: "hidden",
                  padding: "8px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                }}
              >
                {/* ... POS Content (Assume unchanged logic for brevity) ... */}
                {isAdmin && (
                  <div
                    className="w-full h-full border-1 border-200 border-round"
                    style={{
                      background: "#ffffff",
                      overflow: "hidden",
                      fontSize: "13px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {posLoading ? (
                      <div style={{ padding: "1rem" }}>
                        <Skeleton width="100%" height="1.5rem" className="mb-2" />
                        <Skeleton width="100%" height="1.5rem" className="mb-2" />
                        <Skeleton width="100%" height="1.5rem" />
                      </div>
                    ) : posError ? (
                      <p className="text-center text-red-500 mt-4">{posError}</p>
                    ) : posDetails.length > 0 ? (
                      <div style={{ height: "100%", overflowY: "auto", padding: "0.5rem" }}>
                        <table className="w-full">
                          <thead className="bg-green-50">
                            <tr>
                              <th className="p-1 text-left border-bottom-1 border-200">POS</th>
                              <th className="p-1 text-right border-bottom-1 border-200">Avail.Qty</th>
                              <th className="p-1 text-right border-bottom-1 border-200" title="Pending quantity from open orders created in the last 30 days for this POS">Ord.Qty (30d)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {posDetails.map((pos) => (
                              <tr key={pos.posId}>
                                <td className="p-1 border-bottom-1 border-100">{pos.posAlias}</td>
                                <td className="p-1 text-right border-bottom-1 border-100">{pos.quantity}</td>
                                <td className="p-1 text-right border-bottom-1 border-100 font-bold text-bluegray-700">
                                  {pos.assignedOpenOrderQty}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center mt-4">No POS data available</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "12px 14px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <h3
            style={{
              margin: "0",
              fontSize: "14px",
              fontWeight: "600",
              color: "#1f2937",
              lineHeight: "1.2",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            title={productDialog.name}
          >
            {productDialog.name}
          </h3>

          <p
            style={{
              margin: "0",
              fontSize: "12px",
              color: "#6b7280",
              lineHeight: "1.4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            title={productDialog.description}
          >
            {productDialog.description || "Premium quality product"}
          </p>
          {productDialog.name?.toLowerCase().includes("vessel") &&
            !(productDialog.id === 141 && productDialog.subCategoryID === 33) && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                alignSelf: "flex-start",
                padding: "4px 10px",
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#92400e",
                background: "#fffbeb",
                border: "1px solid #fef3c7",
                marginTop: "6px",
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ fontSize: "12px" }}></i>
              Price excluding vessel
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: isCompact ? "8px" : "12px",
              marginTop: "auto",
              paddingTop: "8px",
            }}
          >
            <span
              style={{
                fontSize: priceSize,
                fontWeight: "700",
                color: "#52a83c",
                whiteSpace: "nowrap",
              }}
            >
              {Helpers.formatAmountInINR(productDialog.totalUnitPrice)}
            </span>

            <button
              onClick={handleAddToCart}
              disabled={productData.inStock === 0 || productData.availabilityPending}
              style={{
                padding: isCompact ? "8px 10px" : "10px 16px",
                borderRadius: "10px",
                border: "none",
                background: productDialog.inStock === 0 ? "#e5e7eb" : "#52a83c",
                color: productDialog.inStock === 0 ? "#9ca3af" : "#ffffff",
                cursor: productDialog.inStock === 0 ? "not-allowed" : "pointer",
                fontSize: buttonSize,
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                flex: "1 0 auto",
                maxWidth: isCompact ? "100px" : "140px",
                height: "36px",
                whiteSpace: "nowrap",
              }}
            >
              <i className="pi pi-shopping-cart" style={{ fontSize: buttonSize }} />
              {isCompact ? "Add" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductCardItems;
